import React, { useEffect, useMemo, useRef, useState } from 'react';
import Textarea from '@salesforce/design-system-react/components/textarea';
import Input from '@salesforce/design-system-react/components/input';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '../../../../components/Spinner';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import { authSelector, setUser } from '../../../../store/auth/slice';
import { ColorPicker, comboboxFilterAndLimit } from '@salesforce/design-system-react';
import Button from '@salesforce/design-system-react/components/button';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { phoneRegExp } from '../../../../constants/validation_expressions';
import Combobox from '@salesforce/design-system-react/components/combobox';
import { apiUserMetaMask } from '../../../../services/auth';
import { Toaster, toast } from 'react-hot-toast';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const defaultTagOptions = [
  {
    id: 'trading',
    label: 'Trading',
  },
  {
    id: 'skating',
    label: 'Skating',
  },
  {
    id: 'tomb',
    label: 'Tomb',
  },
];

const Details = ({ account }) => {
  const dispatch = useDispatch();
  const { user, loading } = useSelector(authSelector);
  const formikRef = useRef();
  const [showToast, setShowToast] = useState(false);
  const [tagSelection, setTagSelection] = useState([]);
  const [tagInputText, setTagInputText] = useState('');
  const [formData, setFormData] = useState({});

  const createdDate = useMemo(() => {
    const jsDate = new Date(user.createdAt);
    return `${jsDate.getFullYear()}/${jsDate.getMonth() + 1}/${jsDate.getDate()}`;
  }, [user.createdAt]);

  useEffect(() => {
    const tags = convertToArray(user.tags);
    if (tags.length) setTagSelection(tags);
  }, [user]);

  const convertToArray = (string) => {
    if (!string) return '';

    if (string.includes(',')) {
      const values = string.split(',');
      return values.map((value) => {
        return { label: value.trim(), value: value.trim() };
      });
    } else {
      return [{ label: string.trim(), value: string.trim() }];
    }
  };

  const validationSchema = () => {
    return Yup.object().shape({
      firstName: Yup.string().required('First  Name is required'),
      lastName: Yup.string().required('Last Name is required'),
      contactNumber: Yup.string()
        .required('Contact is required')
        .matches(phoneRegExp, 'Contact Number is not valid'),
    });
  };

  const onFormSubmit = async (values, setSubmitting) => {
    try {
      const result = await apiUserMetaMask(values);
      if (result.status === 200) {
        if (result.data.data) {
          dispatch(setUser(result.data.data));
        }
        toast.success('Successfully updated', {
          id: 'meta-mask-success',
        });
      } else {
        toast.error('Something went wrong!', {
          id: 'meta-mask-error',
        });
      }
    } catch (error) {}

    setSubmitting(false);
  };

  return (
    <div className="home_card">
      {loading ? (
        <Spinner />
      ) : (
        <Formik
          innerRef={formikRef}
          enableReinitialize={true}
          initialValues={{
            firstName: user.first_name,
            lastName: user.last_name,
            email: user.email,
            contactNumber: user.contact_number,
            address: user.address,
            description: user.description,
            tags: convertToArray(user.tags),
            role: user.role,
            color: user.color,
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setShowToast(true);
            setSubmitting(true);
            const data = {
              first_name: values.firstName,
              last_name: values.lastName,
              address: values.address,
              contact_number: values.contactNumber,
              description: values.description,
              tags: tagSelection.map((item) => item.label).join(','),
              color: values.color,
            };

            /**
             * check if key is empty, and remove empty key.
             */
            Object.keys(data).forEach((key) => {
              if (!data[key]) delete data[key];
            });

            onFormSubmit(data, setSubmitting);
          }}
        >
          {(props) => {
            const {
              values,
              touched,
              errors,
              isSubmitting,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            } = props;
            return (
              <IconSettings iconPath="/assets/icons">
                <Form>
                  <Toaster position="top-right" reverseOrder={false} />
                  <div className="add-form">
                    <section className="slds-p-around_large">
                      <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                        <div className="slds-col_padded slds-m-bottom_large">
                          <Input
                            id="firstName"
                            name="firstName"
                            label="First Name"
                            placeholder="John"
                            required
                            value={values.firstName || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classNames(
                              errors.firstName && touched.firstName && 'slds-has-error'
                            )}
                          />
                          {errors.firstName && touched.firstName && (
                            <div className="slds-form-element__help">{errors.firstName}</div>
                          )}
                        </div>
                        <div className="slds-col_padded slds-m-bottom_large">
                          <Input
                            id="lastName"
                            name="lastName"
                            label="Last Name"
                            placeholder="Dow"
                            required
                            value={values.lastName || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classNames(
                              errors.lastName && touched.lastName && 'slds-has-error'
                            )}
                          />
                          {errors.lastName && touched.lastName && (
                            <div className="slds-form-element__help">{errors.lastName}</div>
                          )}
                        </div>
                      </div>
                      <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                        <div className="slds-col_padded slds-m-bottom_large">
                          <Input
                            id="email"
                            name="email"
                            label="Email"
                            placeholder="johndoe@admin.com"
                            required
                            disabled
                            value={values.email || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classNames(
                              errors.email && touched.email && 'slds-has-error'
                            )}
                          />
                          {errors.email && touched.email && (
                            <div className="slds-form-element__help">{errors.email}</div>
                          )}
                        </div>
                        <div className="slds-col_padded slds-m-bottom_large">
                          <Input
                            id="role"
                            name="role"
                            label="Role"
                            placeholder="role"
                            required
                            disabled
                            value={values.role || ''}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                              e.key === 'Enter' && e.preventDefault();
                            }}
                          />
                        </div>
                      </div>
                      <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                        <div className="slds-col_padded slds-m-bottom_large">
                          <Input
                            id="contactNumber"
                            name="contactNumber"
                            label="Contact Number"
                            placeholder="+0 123 456 7890"
                            required
                            value={values.contactNumber || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classNames(
                              errors.contactNumber && touched.contactNumber && 'slds-has-error'
                            )}
                          />
                          {errors.contactNumber && touched.contactNumber && (
                            <div className="slds-form-element__help">{errors.contactNumber}</div>
                          )}
                        </div>
                        <div className="slds-col_padded slds-m-bottom_large">
                          <Input
                            id="address"
                            name="address"
                            label="Address"
                            placeholder="ADGM, Abu Dahbi, UAE"
                            value={values.address || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classNames(
                              errors.address && touched.address && 'slds-has-error'
                            )}
                          />
                          {errors.address && touched.address && (
                            <div className="slds-form-element__help">{errors.address}</div>
                          )}
                        </div>
                      </div>
                      <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                        <div className="slds-col_padded slds-m-bottom_large">
                          <IconSettings iconPath="/assets/icons">
                            <Combobox
                              id="tags"
                              name="tags"
                              optionsAddItem={
                                tagInputText.length > 0
                                  ? [
                                      {
                                        id: 'add-new-tag',
                                        label: (inputValue) => [
                                          inputValue && inputValue.length > 0 ? (
                                            <span style={{ marginLeft: '-30px' }}>
                                              Create "{inputValue}"
                                            </span>
                                          ) : (
                                            ''
                                          ),
                                        ],
                                      },
                                    ]
                                  : []
                              }
                              events={{
                                onChange: (event, data) => {
                                  setTagInputText(data.value);
                                },
                                onSelect: (event, data) => {
                                  const { selection } = data;
                                  if (selection && selection.length > 0) {
                                    if (
                                      selection[selection.length - 1].type &&
                                      selection[selection.length - 1].type === 'footer'
                                    ) {
                                      if (tagInputText.length > 0) {
                                        setTagSelection(
                                          tagSelection.concat({ label: tagInputText })
                                        );
                                        setTagInputText('');
                                      }
                                    } else {
                                      setTagSelection(selection);
                                    }
                                  } else {
                                    setTagSelection(selection.pop());
                                  }
                                },
                                onSubmit: (event, data) => {
                                  if (!data.value.trim()) return;
                                  setTagSelection(tagSelection.concat({ label: data.value }));
                                  setTagInputText('');
                                },
                                onRequestRemoveSelectedOption: (event, data) => {
                                  setTagSelection(data.selection);
                                },
                              }}
                              labels={{
                                label: 'Tags',
                                placeholder: 'Enter tags',
                              }}
                              options={comboboxFilterAndLimit({
                                inputValue: tagInputText,
                                limit: 5,
                                options: defaultTagOptions,
                                selection: tagSelection,
                              })}
                              value={tagInputText}
                              selection={tagSelection}
                              multiple
                            />
                          </IconSettings>
                          {errors.tags && touched.tags && (
                            <div className="slds-form-element__help">{errors.tags}</div>
                          )}
                        </div>
                        <div className="slds-col_padded slds-m-bottom_large">
                          <div className="">
                            <IconSettings iconPath="/assets/icons">
                              <ColorPicker
                                labels={{ label: 'Color' }}
                                className={'slds-form-element__label'}
                                id="color"
                                name="color"
                                menuPosition="overflowBoundaryElement"
                                value={values.color || ''}
                                events={{
                                  onChange: (event, { color }) => {
                                    setFieldValue('color', color);
                                    setFormData({
                                      ...formData,
                                      color,
                                    });
                                  },
                                }}
                              />
                            </IconSettings>
                          </div>
                        </div>
                      </div>
                      <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                        <div className="slds-col_padded" style={{ alignSelf: 'normal' }}>
                          <div className="slds-form-element">
                            <div className="slds-form-element__control">
                              <Textarea
                                id="description"
                                name="description"
                                label="Description"
                                placeholder="Enter description"
                                value={values.description || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    <div style={{ paddingLeft: '25px', paddingBottom: '20px' }}>
                      <Button
                        label={isSubmitting ? <span>Loading</span> : <span>Update</span>}
                        type="submit"
                        variant="brand"
                      />
                    </div>
                  </div>
                </Form>
              </IconSettings>
            );
          }}
        </Formik>
      )}
    </div>
  );
};

export default Details;
